import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api, ApiError } from "./api";
import type { AuthMethod, Role } from "./apiTypes";
import { config } from "./config";

// Minimal Google Identity Services typings — we only use the pieces we need.
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (opts: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            auto_select?: boolean;
            ux_mode?: "popup" | "redirect";
            use_fedcm_for_prompt?: boolean;
            itp_support?: boolean;
          }) => void;
          prompt: () => void;
          disableAutoSelect: () => void;
          renderButton: (
            parent: HTMLElement,
            opts: Record<string, unknown>,
          ) => void;
        };
      };
    };
  }
}

type AuthSource = "google" | "password";

/** Why a fresh Google credential was refused. Currently the server allowlist
 *  is the only gate, so any `/v1/auth/me` rejection maps to "not_allowlisted"
 *  (research R2). Kept as a union so finer outcomes can be added later. */
export type GoogleAuthError = "not_allowlisted";

type AuthUser = {
  /** Active bearer token — a Google ID token OR a Predabet session JWT. The
   *  rest of the app passes this as `idToken` to API calls, unaware of which. */
  idToken: string;
  email: string;
  name: string;
  picture?: string;
  exp: number;
  source: AuthSource;
};

/** Server-asserted account info from /v1/auth/me (research D10). */
type MeInfo = {
  role: Role;
  emailVerified: boolean;
  authMethods: AuthMethod[];
};

type AuthContextValue = {
  user: AuthUser | null;
  role: Role | null;
  ready: boolean;
  signIn: () => void;
  signOut: () => void;
  renderGoogleButton: (el: HTMLElement) => void;
  /** Email+password login. Throws ApiError on bad credentials / unverified. */
  loginWithPassword: (email: string, password: string) => Promise<void>;
  /** Adopt a Predabet session token minted elsewhere (e.g. email verification). */
  adoptSessionToken: (token: string) => void;
  /** True while a Google credential is being verified against the allowlist. */
  googlePending: boolean;
  /** Set when a fresh Google credential was authenticated but refused (FR-018). */
  googleError: GoogleAuthError | null;
  /** Clear the Google sign-in error (e.g. when the dialog is reopened). */
  clearGoogleError: () => void;
  /** Point the shared Google button at an invitation code: the next credential
   *  redeems that invitation instead of attempting a normal login. */
  beginGoogleRedeem: (token: string) => void;
  /** Stop redirecting Google credentials to invitation redemption. */
  cancelGoogleRedeem: () => void;
  /** Error code from a failed redeem-with-Google attempt (server `err.code`,
   *  e.g. "token_expired"), for the redeem page to map to copy. */
  redeemGoogleError: string | null;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const GOOGLE_KEY = "predabet.idtoken.v1";
const SESSION_KEY = "predabet.session.v1";

function decodeJwt(token: string): Record<string, unknown> {
  const [, payload] = token.split(".");
  if (!payload) return {};
  const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "===".slice((normalized.length + 3) % 4);
  try {
    return JSON.parse(atob(padded));
  } catch {
    return {};
  }
}

function userFromToken(token: string, source: AuthSource): AuthUser | null {
  const claims = decodeJwt(token);
  const exp = Number(claims.exp ?? 0);
  if (!exp || exp * 1000 < Date.now()) return null;
  return {
    idToken: token,
    email: String(claims.email ?? ""),
    name: String(claims.name ?? claims.email ?? "user"),
    picture: typeof claims.picture === "string" ? claims.picture : undefined,
    exp,
    source,
  };
}

/** Active identity: a valid session token wins over a Google token. */
function activeUser(
  googleToken: string | null,
  sessionToken: string | null,
): AuthUser | null {
  if (sessionToken) {
    const u = userFromToken(sessionToken, "password");
    if (u) return u;
  }
  if (googleToken) {
    const u = userFromToken(googleToken, "google");
    if (u) return u;
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [googleToken, setGoogleToken] = useState<string | null>(() =>
    localStorage.getItem(GOOGLE_KEY),
  );
  const [sessionToken, setSessionToken] = useState<string | null>(() =>
    localStorage.getItem(SESSION_KEY),
  );
  const [meInfo, setMeInfo] = useState<MeInfo | null>(null);
  const [ready, setReady] = useState(() => !config.googleClientId);
  const [googlePending, setGooglePending] = useState(false);
  const [googleError, setGoogleError] = useState<GoogleAuthError | null>(null);
  const [redeemGoogleError, setRedeemGoogleError] = useState<string | null>(
    null,
  );

  // The bearer we have already resolved /v1/auth/me for, so the effect below
  // doesn't refetch what loginWithPassword / handleCredential just fetched.
  const meFetchedFor = useRef<string | null>(null);

  // Invitation token an in-flight Google credential should redeem, rather than
  // attempt a normal login. GIS's `initialize()` fixes a single callback for
  // every rendered button app-wide, so this is how the redeem page borrows it.
  const redeemTokenRef = useRef<string | null>(null);

  const user = useMemo(
    () => activeUser(googleToken, sessionToken),
    [googleToken, sessionToken],
  );
  const bearer = user?.idToken ?? null;

  const handleCredential = useCallback(async (credential: string) => {
    if (!userFromToken(credential, "google")) return;
    const redeemToken = redeemTokenRef.current;
    if (redeemToken) {
      setGooglePending(true);
      setRedeemGoogleError(null);
      try {
        const res = await api.redeemWithGoogle(redeemToken, credential);
        meFetchedFor.current = res.token;
        localStorage.setItem(SESSION_KEY, res.token);
        setSessionToken(res.token);
        setMeInfo({
          role: res.user.role,
          emailVerified: res.user.email_verified,
          authMethods: res.user.auth_methods,
        });
        redeemTokenRef.current = null;
      } catch (err) {
        setRedeemGoogleError(err instanceof ApiError ? err.code : "unknown");
      } finally {
        setGooglePending(false);
      }
      return;
    }
    setGooglePending(true);
    setGoogleError(null);
    try {
      // Gate on the server allowlist BEFORE admitting: an authenticated but
      // non-allowlisted Google identity must never be persisted as a session
      // (FR-018, research R2). /v1/auth/me 4xxs for identities with no account.
      const me = await api.me({ idToken: credential });
      meFetchedFor.current = credential;
      localStorage.setItem(GOOGLE_KEY, credential);
      setMeInfo({
        role: me.role,
        emailVerified: me.email_verified,
        authMethods: me.auth_methods,
      });
      setGoogleToken(credential);
    } catch {
      // Not allowlisted (or verification failed): discard, persist nothing.
      setGoogleError("not_allowlisted");
    } finally {
      setGooglePending(false);
    }
  }, []);

  const clearGoogleError = useCallback(() => setGoogleError(null), []);

  const beginGoogleRedeem = useCallback((token: string) => {
    redeemTokenRef.current = token;
    setRedeemGoogleError(null);
  }, []);

  const cancelGoogleRedeem = useCallback(() => {
    redeemTokenRef.current = null;
  }, []);

  const adoptSessionToken = useCallback((token: string) => {
    if (!userFromToken(token, "password")) return;
    localStorage.setItem(SESSION_KEY, token);
    setSessionToken(token);
  }, []);

  const loginWithPassword = useCallback(
    async (email: string, password: string) => {
      const res = await api.login(email, password);
      meFetchedFor.current = res.token;
      localStorage.setItem(SESSION_KEY, res.token);
      setSessionToken(res.token);
      setMeInfo({
        role: res.user.role,
        emailVerified: res.user.email_verified,
        authMethods: res.user.auth_methods,
      });
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const clientId = config.googleClientId;
    if (!clientId) {
      return;
    }

    const init = () => {
      if (cancelled || !window.google) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          void handleCredential(response.credential);
        },
        auto_select: false,
        ux_mode: "popup",
        // Required under Chrome's third-party cookie deprecation; without this
        // `prompt()` is silently suppressed in many browsers.
        use_fedcm_for_prompt: true,
        itp_support: true,
      });
      queueMicrotask(() => {
        if (!cancelled) setReady(true);
      });
    };

    if (window.google) {
      init();
    } else {
      const interval = window.setInterval(() => {
        if (window.google) {
          window.clearInterval(interval);
          init();
        }
      }, 100);
      window.setTimeout(() => window.clearInterval(interval), 10_000);
    }

    return () => {
      cancelled = true;
    };
  }, [handleCredential]);

  // Server-asserted role/verification for whichever bearer is active. The
  // session pathway seeds this synchronously in loginWithPassword; this effect
  // covers the Google pathway and page reloads (research D10).
  useEffect(() => {
    if (!bearer) {
      return;
    }
    // Skip the round-trip when we already resolved me() for this exact bearer
    // (fresh Google credential or password login seeds it inline).
    if (meFetchedFor.current === bearer) {
      return;
    }
    let cancelled = false;
    void api
      .me({ idToken: bearer })
      .then((me) => {
        if (cancelled) return;
        meFetchedFor.current = bearer;
        setMeInfo({
          role: me.role,
          emailVerified: me.email_verified,
          authMethods: me.auth_methods,
        });
      })
      .catch(() => {
        // No active account for this identity — leave role unset (non-admin).
        if (!cancelled) setMeInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer]);

  const signIn = useCallback(() => {
    if (!window.google || !config.googleClientId) return;
    window.google.accounts.id.prompt();
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem(GOOGLE_KEY);
    localStorage.removeItem(SESSION_KEY);
    window.google?.accounts.id.disableAutoSelect();
    meFetchedFor.current = null;
    setGoogleToken(null);
    setSessionToken(null);
    setMeInfo(null);
    setGoogleError(null);
    setGooglePending(false);
  }, []);

  // Auto-sign-out when the active token expires while the page is open.
  useEffect(() => {
    if (!user) return;
    const msUntilExpiry = user.exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) {
      queueMicrotask(() => signOut());
      return;
    }
    const timer = window.setTimeout(signOut, msUntilExpiry);
    return () => window.clearTimeout(timer);
  }, [user, signOut]);

  const renderGoogleButton = useCallback((el: HTMLElement) => {
    if (!window.google || !config.googleClientId) return;
    el.innerHTML = "";
    window.google.accounts.id.renderButton(el, {
      type: "standard",
      theme: "filled_black",
      size: "large",
      text: "signin_with",
      shape: "pill",
      logo_alignment: "left",
      width: 220,
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      // Guard on the active bearer so a stale role never leaks once signed out.
      role: bearer ? (meInfo?.role ?? null) : null,
      ready,
      signIn,
      signOut,
      renderGoogleButton,
      loginWithPassword,
      adoptSessionToken,
      googlePending,
      googleError,
      clearGoogleError,
      beginGoogleRedeem,
      cancelGoogleRedeem,
      redeemGoogleError,
    }),
    [
      user,
      bearer,
      meInfo,
      ready,
      signIn,
      signOut,
      renderGoogleButton,
      loginWithPassword,
      adoptSessionToken,
      googlePending,
      googleError,
      clearGoogleError,
      beginGoogleRedeem,
      cancelGoogleRedeem,
      redeemGoogleError,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
